<template>
  <div
    class="audio-recorder bg-brand-color pointer"
    :class="{ 'pulsating-circle': isRecording }"
    @click="toggleRecording"
  >
    <div class="d-flex align-items-center">
      <div>
        <span v-if="isRecording">
          <i class="audio-recorder__button far fa-stop-circle c-white"></i>
        </span>
        <span v-else>
          <i class="audio-recorder__button fas fa-microphone c-white"></i>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { Mp3MediaRecorder } from 'mp3-mediarecorder';
import mp3RecorderWorker from 'workerize-loader!../../utils/worker.js';

const supportsWasm =
  typeof WebAssembly === 'object' &&
  typeof WebAssembly.instantiate === 'function';
const supportsUserMediaAPI =
  navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === 'function';
const isMp3RecordingSupported = supportsWasm && supportsUserMediaAPI;

export default Vue.extend({
  name: 'HMAudioRecorder',
  props: {
    stream: null,
  },
  data() {
    return {
      audioChunks: [],
      mediaRecorder: null,
      currentAudio: {},
      isRecording: false,
      worker: null,
    };
  },
  methods: {
    async initAudioRecorder() {
      if (isMp3RecordingSupported || !this.worker) {
        console.log('MP3 recording supported');
        this.mediaRecorder = new Mp3MediaRecorder(this.stream, {
          worker: this.worker,
        });

        this.mediaRecorder.addEventListener('dataavailable', event => {
          this.audioChunks.push(event.data);
        });

        this.mediaRecorder.addEventListener('stop', event => {
          const mimeType = 'audio/mpeg';
          const blob = new File(this.audioChunks, `audio.mp3`, {
            type: mimeType,
          });
          this.setCurrentRecord(blob);
          this.audioChunks = [];
        });
      } else {
        this.mediaRecorder = new MediaRecorder(this.stream);
        this.mediaRecorder.addEventListener('dataavailable', event => {
          this.audioChunks.push(event.data);
        });

        this.mediaRecorder.addEventListener('stop', () => {
          let mimeType = 'audio/webm';
          if (this.mediaRecorder.mimeType) {
            mimeType = this.mediaRecorder.mimeType;
          }
          const blob = new File(
            this.audioChunks,
            `audio.${mimeType.match(/\/([\w\d]+);?/)[1]}`,
            { type: this.mediaRecorder.mimeType }
          );

          this.setCurrentRecord(blob);
          this.audioChunks = [];
        });
      }
    },
    toggleRecording() {
      if (this.isRecording) {
        this.stop();
      } else {
        this.start();
      }
    },
    start() {
      this.mediaRecorder.start();
      this.isRecording = true;
    },
    stop() {
      this.mediaRecorder.stop();
      this.isRecording = false;
    },
    setCurrentRecord(file) {
      this.$emit('onRecordingAvailable', file);
    },
    initWorker() {
      if (!this.worker) {
        this.worker = mp3RecorderWorker();
      }
    },
  },
  async created() {
    this.initWorker();
  },
  watch: {
    stream: {
      handler(value) {
        this.initWorker();
        this.initAudioRecorder();
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.audio-recorder {
  width: 120px;
  height: 120px;
  border-radius: 50%;

  &__button {
    font-size: 24px;
    cursor: pointer;
  }

  &__wave {
    height: 40px;
    color: $medium-gray;
  }
}

.pulsating-circle {
  width: 120px;
  height: 120px;

  animation: pulse-ring 2.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(0.8);
  }
  50% {
    transform: scale(1);
  }
  75% {
    transform: scale(0.8);
  }
}
</style>
